import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import ShowTicketOpen from "../ShowTicketOpenModal";

const useStyles = makeStyles((theme) => ({
  online: {
    fontSize: 11,
    color: "#25d366"
  },
  offline: {
    fontSize: 11,
    color: "#e1306c"
  }
}));

const filter = createFilterOptions({
  trim: true,
});

const cleanPhone = value => String(value || "").replace(/\D/g, "");
const isPhoneInput = value => cleanPhone(value).length >= 8;
const hasOnlyPhoneChars = value => /^[\d\s()+-]+$/.test(String(value || "").trim());

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          // .get(`/whatsapp/filter`, { params: { companyId, session: 0, channel: channelFilter } })
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => setWhatsapps(data));

          // .then(({ data }) => {
          //   const mappedWhatsapps = data.map((whatsapp) => ({
          //     ...whatsapp,
          //     selected: false,
          //   }));
          //   setWhatsapps(mappedWhatsapps);
          //   if (channelFilter && mappedWhatsapps.length && mappedWhatsapps?.length === 1 && (user.whatsappId === null || user?.whatsapp?.channel !== channelFilter)) {
          //     setSelectedWhatsapp(mappedWhatsapps[0].id)
          //   }
          // });
      };

      if (whatsappId !== null && whatsappId !== undefined) {
        setSelectedWhatsapp(whatsappId)
      }

      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id)
      }
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      default:
        return "error";
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const getOpenTicketFromError = err => {
    const errorData = err?.response?.data || {};
    if (errorData.ticket) return errorData.ticket;

    if (typeof errorData.error === "string") {
      try {
        return JSON.parse(errorData.error);
      } catch (_) {
        return null;
      }
    }

    return null;
  };

  const normalizeContactResponse = data => {
    if (data?.id) return data;
    if (data?.contact?.id) return data.contact;
    if (data?.data?.id) return data.data;
    return null;
  };

  const buildDirectContactFromInput = value => {
    const number = cleanPhone(value);
    if (!number) return null;

    return {
      name: number,
      number,
      channel: "whatsapp",
      isDirectNumber: true,
    };
  };

  const findExistingContactByNumber = async number => {
    const normalizedNumber = cleanPhone(number);
    if (!normalizedNumber) return null;

    const localContact = options.find(option => cleanPhone(option?.number) === normalizedNumber);
    if (localContact?.id) return localContact;

    const { data } = await api.get("contacts", {
      params: { searchParam: normalizedNumber, channel: "whatsapp", isGroup: "false" },
    });
    return data.contacts?.find(contact => cleanPhone(contact?.number) === normalizedNumber) || null;
  };

  const ensureContact = async contact => {
    if (contact?.id) return contact;

    const number = cleanPhone(contact?.number || contact?.name || searchParam);
    if (!number) return null;

    const existingContact = await findExistingContactByNumber(number);
    if (existingContact?.id) return existingContact;

    try {
      const { data } = await api.post("/contacts", {
        name: contact?.name && !isPhoneInput(contact.name) ? contact.name : number,
        number,
        whatsappId: selectedWhatsapp !== "" ? selectedWhatsapp : undefined,
        reuseExisting: true,
      });

      const createdContact = normalizeContactResponse(data);
      if (!createdContact?.id) {
        throw new Error("ERR_CONTACT_CREATED_WITHOUT_ID");
      }

      return createdContact;
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data?.message || "";
      if (String(message).toLowerCase().includes("contact already exists") || String(message).includes("ERR_DUPLICATED_CONTACT")) {
        return findExistingContactByNumber(number);
      }
      throw err;
    }
  };

  const handleSaveTicket = async contact => {
    // if (selectedQueue === "" && user.profile !== 'admin') {
    if (selectedQueue === "") {
      toast.error("Selecione uma fila");
      return;
    }

    setLoading(true);
    try {
      const contactToUse = await ensureContact(contact || selectedContact || buildDirectContactFromInput(searchParam));
      if (!contactToUse?.id) {
        toast.error("Informe um telefone valido para iniciar o atendimento");
        return;
      }

      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactToUse.id,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      const ticket = getOpenTicketFromError(err);

      if (ticket?.id || ticket?.uuid) {
        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name || "outro atendente");
          setQueueTicketOpen(ticket?.queue?.name || "");
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
          onClose(ticket);
        }
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (e, newValue) => {
    if (typeof newValue === "string" && isPhoneInput(newValue)) {
      const number = cleanPhone(newValue);
      setSelectedContact({ name: number, number, channel: "whatsapp", isDirectNumber: true });
      setChannelFilter("whatsapp");
      return;
    }

    if (newValue?.number) {
      setSelectedContact(newValue);
      setChannelFilter(newValue.channel || "whatsapp");
    } else if (newValue?.name && !newValue.isIncompleteNumber) {
      if (newValue.isDirectNumber || isPhoneInput(newValue.name)) {
        const number = cleanPhone(newValue.number || newValue.name);
        setSelectedContact({ name: number, number, channel: "whatsapp", isDirectNumber: true });
        setChannelFilter("whatsapp");
      } else {
        setNewContact({ name: newValue.name });
        setContactModalOpen(true);
      }
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      if (isPhoneInput(params.inputValue)) {
        const number = cleanPhone(params.inputValue);
        filtered.push({
          name: number,
          number,
          channel: "whatsapp",
          isDirectNumber: true,
        });
        return filtered;
      }

      if (hasOnlyPhoneChars(params.inputValue)) {
        filtered.push({
          name: cleanPhone(params.inputValue) || params.inputValue,
          isIncompleteNumber: true,
        });
        return filtered;
      }

      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = option => {
    if (option.isIncompleteNumber) {
      return (
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", color: "#666", lineHeight: "2" }}>
          Digite o telefone completo com DDD
        </Typography>
      );
    }

    if (option.isDirectNumber) {
      return <>
        {IconChannel("whatsapp")}
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
          Chamar {option.number} sem cadastro manual
        </Typography>
      </>
    }

    if (option.number) {
      return <>
        {IconChannel(option.channel)}
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
          {option.name} - {option.number}
        </Typography>
      </>
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = option => {
    if (option.isIncompleteNumber) {
      return "Digite o telefone completo com DDD";
    }

    if (option.isDirectNumber) {
      return `Chamar ${option.number}`;
    }

    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {                     
              setChannelFilter(newValue ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue)
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading) return;
                  if (e.key === "Enter") {
                    const directContact = selectedContact || (isPhoneInput(searchParam) ? {
                      name: cleanPhone(searchParam),
                      number: cleanPhone(searchParam),
                      channel: "whatsapp",
                      isDirectNumber: true,
                    } : null);
                    if (directContact) handleSaveTicket(directContact);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )
    }
    return null;
  }

  return (
    <>

      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id="form-dialog-title">
          {i18n.t("newTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid style={{ width: 300 }} container spacing={2}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {/* FILA */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === "") {
                    return "Selecione uma fila"
                  }
                  const queue = user.queues.find(q => q.id === selectedQueue)
                  return queue.name
                }}
              >
                {user.queues?.length > 0 &&
                  user.queues.map((queue, key) => (
                    <MenuItem dense key={key} value={queue.id}>
                      <ListItemText primary={queue.name} />
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
            {/* CONEXAO */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedWhatsapp}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                    return "Selecione uma Conexão"
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                  return whatsapp?.name
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <ListItemText
                        primary={
                          <>
                            {IconChannel(whatsapp.channel)}
                            <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
                              {whatsapp.name} &nbsp; <p className={(whatsapp.status) === 'CONNECTED' ? classes.online : classes.offline} >({whatsapp.status})</p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="button"
            disabled={!selectedContact && !isPhoneInput(searchParam)}
            onClick={() => handleSaveTicket(selectedContact || buildDirectContactFromInput(searchParam))}
            color="primary"
            loading={loading}
          >
            {i18n.t("newTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
          ></ContactModal>
        )}
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog >
    </>
  );
};
export default NewTicketModal;
